import React, { useState, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRightIcon, DocumentTextIcon, SearchIcon, ClockIcon, ChatAltIcon, CheckIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import FormDetailsPanel from './FormDetailsPanel';
import ProviderManageInformantsForm from './ProviderManageInformantsForm';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { formatDateTime } from '../../../../utils/timestampUtils';
import InfoTooltip from '../../../../components/InfoTooltip';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Scatter } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
  ChartDataLabels
);

/**
 * Whether a scaleResults blob has anything the overview row can display.
 *
 * WHY THIS GUARD EXISTS:
 * A form can be marked completed (completedAt / progress 100%) while
 * scaleResults is still null — common for modular autism scales (e.g. TOC's
 * CAT-Q) that were finished before scoring was wired into GenericFormPage, or
 * where scoring failed silently. Dereferencing scaleResults.score in that case
 * blank-screens the whole Patient Overview. Treat a missing blob as "no score
 * to show" and fall through to a Completed/Not-started label instead.
 */
export function hasDisplayableScaleResults(scaleResults) {
  if (!scaleResults || typeof scaleResults !== 'object') return false;
  return (
    scaleResults.score !== undefined
    || scaleResults.totalScore !== undefined
    || Boolean(scaleResults.sectionResults)
    || Boolean(scaleResults.scores)
    || Boolean(scaleResults.quadrant_scores)
  );
}

// BDEFS Dot Plot with Range Bars Component
const BdefsPercentileChart = ({ scaleResults }) => {
  if (!scaleResults?.sectionResults) return null;

  const sections = [
    { key: 'selfManagementTime', name: 'Self-Management To Time' },
    { key: 'selfOrganisation', name: 'Self Organisation / Problem Solving' },
    { key: 'selfRestraint', name: 'Self-Restraint' },
    { key: 'selfMotivation', name: 'Self-Motivation' },
    { key: 'selfRegulationEmotion', name: 'Self-Regulation of Emotions' }
  ];

  // Filter to only completed sections with percentiles
  const completedSections = sections
    .map(section => ({
      ...section,
      data: scaleResults.sectionResults[section.key]
    }))
    .filter(section => section.data?.complete && section.data?.percentile !== null);

  // Add ADHD Likelihood if available
  const adhdData = scaleResults.summary?.adhdLikelihood;
  if (adhdData?.complete && adhdData?.percentile !== null) {
    completedSections.push({
      key: 'adhdLikelihood',
      name: 'ADHD Likelihood',
      data: {
        ...adhdData,
        percentile: adhdData.percentile
      }
    });
  }

  if (completedSections.length === 0) {
    return (
      <div className="mt-4 p-4 bg-gray-50 border rounded-lg text-center">
        <p className="text-sm text-gray-600">No percentile data available</p>
      </div>
    );
  }

  // Get color based on percentile value
  const getPercentileColor = (percentile) => {
    let value;
    if (Array.isArray(percentile)) {
      value = (percentile[0] + percentile[1]) / 2; // Use midpoint for ranges
    } else {
      value = percentile;
    }

    if (value >= 90) return '#ef4444'; // Red (90th+ percentile)
    if (value >= 76) return '#f97316'; // Orange (76-89th percentile)
    if (value >= 51) return '#eab308'; // Yellow (51-75th percentile)
    return '#10b981'; // Green (0-50th percentile)
  };

  // Create separate datasets for each range line and dots
  const rangeDatasets = completedSections
    .map((section, index) => {
      const percentile = section.data.percentile;
      if (Array.isArray(percentile)) {
        return {
          type: 'line',
          label: `${section.name} Range`,
          data: [
            { x: percentile[0], y: section.name },
            { x: percentile[1], y: section.name }
          ],
          backgroundColor: 'transparent',
          borderColor: getPercentileColor(section.data.percentile),
          borderWidth: 4,
          borderDash: [8, 4],
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          showLine: true,
          tension: 0
        };
      }
      return null;
    })
    .filter(dataset => dataset !== null);

  const chartData = {
    datasets: [
      ...rangeDatasets,
      // Dots for endpoints of ranges and single values
      {
        type: 'scatter',
        label: 'Percentile Points',
        data: completedSections.flatMap((section, index) => {
          const percentile = section.data.percentile;
          if (Array.isArray(percentile)) {
            // Create dots at both ends of the range
            return [
              {
                x: percentile[0], 
                y: section.name,
                percentileRange: percentile,
                sectionData: section.data,
                sectionName: section.name,
                isEndpoint: true
              },
              {
                x: percentile[1], 
                y: section.name,
                percentileRange: percentile,
                sectionData: section.data,
                sectionName: section.name,
                isEndpoint: true
              }
            ];
          } else {
            // Single dot for single percentile
            return [{
              x: percentile,
              y: section.name,
              percentileRange: null,
              sectionData: section.data,
              sectionName: section.name,
              isEndpoint: false
            }];
          }
        }),
        backgroundColor: completedSections.flatMap(section => {
          const color = getPercentileColor(section.data.percentile);
          return Array.isArray(section.data.percentile) ? [color, color] : [color];
        }),
        borderColor: completedSections.flatMap(section => {
          const color = getPercentileColor(section.data.percentile);
          return Array.isArray(section.data.percentile) ? [color, color] : [color];
        }),
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30,
        bottom: 20,
        left: 10,
        right: 40
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context) => {
            return context[0].raw.sectionName;
          },
          label: (context) => {
            const data = context.raw;
            const labels = [];
            
            if (data.percentileRange) {
              labels.push(`Percentile Range: ${data.percentileRange[0]}-${data.percentileRange[1]}th`);
            } else {
              labels.push(`Percentile: ${data.x}th`);
            }
            
            labels.push(`Raw Score: ${data.sectionData.score}`);
            labels.push(`Valid Answers: ${data.sectionData.validAnswers}/${data.sectionData.totalQuestions}`);
            
            return labels;
          }
        }
      },
      datalabels: {
        display: function(context) {
          // Only show labels on the scatter dataset (last dataset with scatter type)
          return context.dataset.type === 'scatter';
        },
        anchor: 'center',
        align: 'right',
        formatter: (value, context) => {
          // Show label only for single points or the rightmost point of ranges
          if (!value.percentileRange) {
            // Single value
            return value.x.toString();
          } else if (value.percentileRange && value.x === value.percentileRange[1]) {
            // Rightmost point of range
            return `${value.percentileRange[0]}-${value.percentileRange[1]}`;
          }
          return ''; // Don't show label for leftmost endpoint
        },
        color: '#374151',
        font: {
          size: 11,
          weight: 'bold'
        },
        offset: 8
      },
      annotation: {
        annotations: {
          // Remove vertical reference lines
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: '#f3f4f6',
          drawBorder: false,
        },
        ticks: {
          color: '#6b7280',
          font: { size: 12 },
          stepSize: 10,
          callback: function(value) {
            return value + 'th';
          }
        },
        title: {
          display: true,
          text: 'Percentile',
          color: '#374151',
          font: { size: 14, weight: 'bold' }
        }
      },
      y: {
        type: 'category',
        position: 'left',
        labels: completedSections.map(section => section.name),
        grid: {
          display: false,
        },
        ticks: {
          color: '#374151',
          font: { size: 12, weight: '500' },
          maxRotation: 0,
          minRotation: 0
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'point'
    }
  };

  // Check if any sections have percentile ranges (arrays)
  const hasPercentileRanges = completedSections.some(section => 
    Array.isArray(section.data.percentile)
  );

  // Get demographic information for lookup table details
  const getAgeRangeDisplay = (ageRange) => {
    const ageRangeMap = {
      '18_34': 'ages 18-34',
      '35_49': 'ages 35-49', 
      '50_64': 'ages 50-64',
      '65_81': 'ages 65-81'
    };
    return ageRangeMap[ageRange] || ageRange;
  };

  const getSexDisplay = (sex) => {
    return sex; // Return as-is without case conversion
  };

  // Extract demographic info from scale results
  const patientAge = scaleResults.patientAge;
  const patientSex = scaleResults.patientSex;
  const lookupTableUsed = scaleResults.lookupTableUsed;

  return (
    <div className="my-4 p-6 bg-white border rounded-lg shadow-sm">      
      
      <div style={{ height: `${Math.max(350, completedSections.length * 70)}px` }}>
        <Scatter data={chartData} options={options} />
      </div>
      
      <div className="mt-4 text-xs text-gray-500">
        <p>
          Higher percentiles indicate greater executive functioning difficulties. 
          {hasPercentileRanges 
            ? 'Horizontal bars show percentile ranges; single dots show exact percentile values.'
            : 'Dots show percentile values.'
          }
          {lookupTableUsed && patientAge && patientSex && (
            <span>
              {' '}Percentile scores have been calculated using the {getAgeRangeDisplay(patientAge)} {getSexDisplay(patientSex)} lookup table.
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

// WEISS Horizontal Bar Chart Component
const WeissBarChart = ({ scaleResults }) => {
  if (!scaleResults?.sectionResults) return null;

  const sections = [
    { key: 'family', name: 'Family' },
    { key: 'work', name: 'Work' },
    { key: 'education', name: 'Education' },
    { key: 'lifeSkills', name: 'Life Skills' },
    { key: 'selfConcept', name: 'Self-Concept' },
    { key: 'social', name: 'Social' },
    { key: 'risk', name: 'Risk' }
  ];

  // Filter to only completed sections and prepare data
  const completedSections = sections
    .map(section => ({
      ...section,
      data: scaleResults.sectionResults[section.key]
    }))
    .filter(section => section.data?.complete && section.data?.averageScore !== null);

  if (completedSections.length === 0) {
    return (
      <div className="mt-4 p-4 bg-gray-50 border rounded-lg text-center">
        <p className="text-sm text-gray-600">No completed sections to display</p>
      </div>
    );
  }

  const chartData = {
    labels: completedSections.map(section => section.name),
    datasets: [
      {
        data: completedSections.map(section => section.data.averageScore),
        backgroundColor: completedSections.map(section => 
          section.data.isImpaired ? '#ef4444' : '#10b981'
        ),
        borderColor: completedSections.map(section => 
          section.data.isImpaired ? '#dc2626' : '#059669'
        ),
        borderWidth: 1,
        borderRadius: 0, // Remove rounded corners
        borderSkipped: false,
      }
    ]
  };

  const options = {
    indexAxis: 'y', // This makes it horizontal
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30,
        bottom: 10,
        left: 10,
        right: 60
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context) => {
            const section = completedSections[context[0].dataIndex];
            return section.name;
          },
          label: (context) => {
            const section = completedSections[context.dataIndex];
            const score = section.data.averageScore.toFixed(2);
            const status = section.data.isImpaired ? 'Impaired' : 'Not Impaired';
            return [
              `Score: ${score}`,
              `Status: ${status}`,
              `Valid Answers: ${section.data.validAnswers}/${section.data.totalQuestions}`
            ];
          }
        }
      },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'end',
        formatter: (value) => {
          return value.toFixed(1);
        },
        color: '#374151',
        font: {
          size: 12,
          weight: 'bold'
        },
        offset: 8
      },
      annotation: {
        annotations: {
          thresholdLine: {
            type: 'line',
            xMin: 1.5,
            xMax: 1.5,
            borderColor: '#dc2626',
            borderWidth: 2,
            borderDash: [5, 5],            
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 3,
        grid: {
          color: '#e5e7eb',
          drawBorder: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12
          },
          stepSize: 0.5
        },
        title: {
          display: false // Hide the "Average Score" title
        }
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#374151',
          font: {
            size: 12,
            weight: '500'
          }
        }
      }
    }
  };

  return (
    <div className="my-4 p-6 bg-white border rounded-lg shadow-sm">     
      
      <div style={{ height: `${Math.max(300, completedSections.length * 50)}px` }}>
        <Bar data={chartData} options={options} />
      </div>
      
      <div className="mt-4 text-xs text-gray-500">
        <p>
          Scores above 1.5 indicate functional impairment in that domain. 
          Chart shows average scores across answered questions in each section.
        </p>
      </div>
    </div>
  );
};

export default function PatientScalesPanel({ patientData, informantData = {}, displayMode = 'full', isSubclinicalService = false, hideInformantScales = false }) {
  const { patientId } = useParams();
  const { fetchPatientData } = useContext(PatientDataContext);
  const [selectedForm, setSelectedForm] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [isInformantFeedbackDialogOpen, setIsInformantFeedbackDialogOpen] = useState(false);
  const [isManageInformantsOpen, setIsManageInformantsOpen] = useState(false);

  // Toggle row expansion
  const toggleRowExpansion = (key) => {
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Get all scales from metadata
  const allPatientScales = useMemo(() => {
    if (!patientData?.dataModel?.['1_preAppointmentInformation']?.sections) {
      return [];
    }
    
    // Find the Patient Scales section
    const patientScalesSection = Object.values(patientData.dataModel['1_preAppointmentInformation'].sections)
      .find(section => section.internalName === "Patient Scales");
    
    if (!patientScalesSection || !patientScalesSection.domains) {
      return [];
    }
    
    // Get all domains and sort by suggestedOrder
    return Object.values(patientScalesSection.domains)
      .sort((a, b) => a.suggestedOrder - b.suggestedOrder)
      .map(domain => ({
        key: domain.varName,
        title: domain.displayName,
        internalName: domain.internalName,
        tooltipText: domain.tooltipText,
        completed: false,
        // Default empty values
        scaleResults: null
      }));
  }, [patientData]);

  // Get all informant scales from metadata and augment with any forms present on informant docs
  const allInformantScales = useMemo(() => {
    if (!patientData?.dataModel?.['1_preAppointmentInformation']?.sections) {
      return [];
    }
    
    // Find the Informant Scales section
    const informantScalesSection = Object.values(patientData.dataModel['1_preAppointmentInformation'].sections)
      .find(section => section.internalName === "Informant Scales");
    
    if (!informantScalesSection || !informantScalesSection.domains) {
      return [];
    }        
    
    // Base domains from patient data model
    const base = Object.values(informantScalesSection.domains)
      .map(domain => ({
        key: domain.varName,
        title: domain.displayName,
        internalName: domain.internalName,
        tooltipText: domain.tooltipText,
        isInformantScale: true,
        completed: false,
        scaleResults: null
      }));

    // Augment with any informant forms present on linked informants but missing from base domains
    try {
      // Map of all known informant form keys to their display titles
      // Adulthood: BAARS-IV Current or ASRS-O (org-specific)
      // Childhood: BAARS-IV Childhood or SNAP-IV (org-specific)
      const knownTitles = {
        baarsIVCurrentInformant: 'BAARS-IV Current - Informant',
        asrsOAdulthoodInformant: 'ASRS-O - Informant',
        baarsIVChildhoodInformant: 'BAARS-IV Childhood - Informant',
        snapIVChildhoodInformant: 'SNAP-IV Childhood - Informant',
      };
      const knownInformantForms = new Set(Object.keys(knownTitles));
      const existingKeys = new Set(base.map(s => s.key));

      Object.entries(informantData || {}).forEach(([infId, inf]) => {
        const formKeys = Object.keys(inf?.formData || {});
        formKeys.forEach(k => {
          if (knownInformantForms.has(k) && !existingKeys.has(k)) {
            base.push({
              key: k,
              title: knownTitles[k] || k,
              internalName: knownTitles[k] || k,
              tooltipText: undefined,
              isInformantScale: true,
              completed: false,
              scaleResults: null,
            });
            existingKeys.add(k);
          }
        });
      });
    } catch (e) {
      console.warn('Failed to augment informant scales from informantData:', e);
    }

    return base;
  }, [patientData, informantData]);

  // Enhance with completed results for patient scales
  const enhancedPatientScales = useMemo(() => {
    if (!allPatientScales.length) return [];
    
    // Clone the array
    const result = [...allPatientScales];
    
    // Map of scales by key for quick lookup
    const scaleMap = {};
    result.forEach((scale, index) => {
      scaleMap[scale.key] = index;
    });
    
    // Update with completed data and status from forms
    const formData = patientData?.formData || {};
    Object.entries(formData).forEach(([formKey, form]) => {
      if (scaleMap[formKey] !== undefined) {
        const index = scaleMap[formKey];
        
        // Determine form status like in exportUtils.
        // Modular autism formData stores progress as { progressPercentage }
        // (see buildFormDataEntryFromInstance); ADHD formData stores a bare
        // number (or 100). Accept both so demo-prefilled autism scales that
        // carry progressPercentage: 100 are not shown as "Not started" when
        // completedAt / scaleResults.complete are briefly absent.
        const progressPercentage = typeof form.progress === 'number'
          ? form.progress
          : form.progress?.progressPercentage;
        const isCompleted = Boolean(
          form.completedAt ||
          progressPercentage === 100 ||
          form.scaleResults?.complete === true
        );
        const isStarted = Boolean(
          form.startedAt ||
          form.firstStartedAt ||
          form.lastUpdated ||
          (progressPercentage > 0) ||
          (form.scaleResults && !form.scaleResults.complete)
        );
        
        result[index] = {
          ...result[index],
          completed: isCompleted,
          isStarted: isStarted,
          isCompleted: isCompleted,
          progress: form.progress,
          scaleResults: form.scaleResults || null
        };
      }
    });
    
    return result;
  }, [allPatientScales, patientData]);

  // Sort patient scales: ASRS first, then BAARS, then everything else (by original order)
  const sortedPatientScales = useMemo(() => {
    const getScalePriority = (scale) => {
      const key = scale.key?.toLowerCase() || '';
      const name = scale.internalName?.toLowerCase() || '';
      // ASRS comes first
      if (key === 'asrs' || name.includes('asrs')) return 0;
      // BAARS-IV scales come second
      if (key.includes('baars') || name.includes('baars')) return 1;
      // Everything else
      return 2;
    };

    return [...enhancedPatientScales].sort((a, b) => {
      const priorityA = getScalePriority(a);
      const priorityB = getScalePriority(b);
      // If priorities differ, sort by priority
      if (priorityA !== priorityB) return priorityA - priorityB;
      // Otherwise maintain original order (from suggestedOrder)
      return 0;
    });
  }, [enhancedPatientScales]);


  // Helper function to determine if an informant should have a particular scale
const informantHasScaleAccess = (scaleKey, informant, patientData) => {
  // Get the informant's assigned life stages from the patient document
  const informantId = informant.id || Object.keys(informant)[0];
  const informantInfo = patientData?.informants?.[informantId];
  const assignedLifeStages = informantInfo?.assignedForLifeStages || [];
  
  // Map scales to life stages
  // Adulthood: BAARS-IV Current or ASRS-O (org-specific)
  // Childhood: BAARS-IV Childhood or SNAP-IV (org-specific)
  const scaleLifeStageMap = {
    'baarsIVCurrentInformant': 'Adulthood',
    'asrsOAdulthoodInformant': 'Adulthood',
    'baarsIVChildhoodInformant': 'Childhood',
    'snapIVChildhoodInformant': 'Childhood'
  };
  
  // Check if the informant is assigned to the life stage for this scale
  const requiredLifeStage = scaleLifeStageMap[scaleKey];
  
  // Alternative check: see if the form exists in the informant's formData
  const hasFormInData = informant.formData && scaleKey in informant.formData;
  
  return hasFormInData || (requiredLifeStage && assignedLifeStages.includes(requiredLifeStage));
};

// Enhance with completed results for informant scales
// Design notes:
// - We scope informants strictly to those referenced on the current patient
//   (patientData.informants) to avoid cross-patient bleed when navigating.
// - We still compute started/completed states from the informant's own formData,
//   but never show forms for informants who aren't linked to this patient.
const enhancedInformantScales = useMemo(() => {
  if (!allInformantScales.length) return [];
  
  // Create a flat array of all informant scales with their results
  const result = [];
  
  // For each informant returned by PatientOverview informant fetch
  Object.entries(informantData).forEach(([informantId, informant]) => {
    // Only include informants that are actually referenced by the current patient
    if (!patientData?.informants || !patientData.informants[informantId]) {
      return;
    }
    // Skip if informant has no form data
    if (!informant?.formData) return;
    
    // For each informant scale
    allInformantScales.forEach(scale => {
      // Check if this informant should have this scale based on their life stage assignment
      const shouldHaveScale = informantHasScaleAccess(scale.key, informant, patientData);
      
      // Skip scales that this informant shouldn't have
      if (!shouldHaveScale) return;
      
      // Check if this informant has completed this scale
      const formData = informant.formData[scale.key];
      
      // Determine form status like in exportUtils
      const isCompleted = formData?.completedAt || formData?.progress === 100 || formData?.scaleResults?.complete === true;
      const isStarted = formData?.startedAt || formData?.firstStartedAt || formData?.lastUpdated || 
                       (formData?.progress && formData.progress.progressPercentage > 0) || 
                       (formData?.scaleResults && !formData.scaleResults.complete) ||
                       (formData && Object.keys(formData).length > 0); // has any form data
      
      // Add scale with informant details and status
      result.push({
        ...scale,
        completed: isCompleted,
        isStarted: isStarted,
        isCompleted: isCompleted,
        progress: formData?.progress,
        scaleResults: formData?.scaleResults || null,
        informantId,
        informantName: informant.displayName || informant.firstName || informant.email || 'Informant',
        relationshipToPatient: patientData?.informants?.[informantId]?.informantRelationshipToPatient || 'Unknown'
      });
    });
  });
  
  return result;
}, [allInformantScales, informantData, patientData]);

  // Consistent ordering: Adulthood first, then Childhood (regardless of instrument)
  const sortedInformantScales = useMemo(() => {
    const lifeStagePriority = (key) => {
      // Adulthood forms (BAARS-IV Current or ASRS-O) come first
      if (key === 'baarsIVCurrentInformant' || key === 'asrsOAdulthoodInformant') return 0;
      // Childhood forms (BAARS-IV Childhood or SNAP-IV) come second
      if (key === 'baarsIVChildhoodInformant' || key === 'snapIVChildhoodInformant') return 1;
      return 2; // Others (if any)
    };
    return [...enhancedInformantScales].sort((a, b) => {
      const pa = lifeStagePriority(a.key);
      const pb = lifeStagePriority(b.key);
      if (pa !== pb) return pa - pb;
      // Stable secondary: by internalName then informantName for deterministic ordering
      const nameA = (a.internalName || a.title || a.key || '').toLowerCase();
      const nameB = (b.internalName || b.title || b.key || '').toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      const infA = (a.informantName || '').toLowerCase();
      const infB = (b.informantName || '').toLowerCase();
      return infA.localeCompare(infB);
    });
  }, [enhancedInformantScales]);

  const handleFormClick = (formKey) => {
    setSelectedForm(formKey);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
  };

  // Get severity class based on scale and severity
  const getSeverityClass = (scale, severity) => {
    const classes = {
      base: "px-2 py-1 rounded-full text-xs font-medium",
      minimal: "bg-green-100 text-green-800",
      mild: "bg-yellow-100 text-yellow-800",
      moderate: "bg-orange-100 text-orange-800",
      severe: "bg-red-100 text-red-800",
      default: "bg-gray-100 text-gray-800",
      clinically_significant: "bg-orange-100 text-orange-800",
      not_clinically_significant: "bg-gray-100 text-gray-800",
      normal: "bg-green-100 text-green-800",
      at_risk: "bg-orange-100 text-orange-800",
      // Violet for scales with binary thresholds where score is below clinical cutoff
      below_threshold: "bg-violet-100 text-violet-800"
    };
    
    if (!severity) return `${classes.base} ${classes.default}`;
    
    const severityLower = severity.toLowerCase();
    // CORE-10 specific: "Healthy" and "Low level" need explicit handling
    // Use includes() to handle variations like "Low level distress" from older data
    if (severityLower === "healthy") return `${classes.base} ${classes.minimal}`; // green
    if (severityLower.includes("low level")) return `${classes.base} ${classes.mild}`; // yellow
    if (severityLower.includes("minimal")) return `${classes.base} ${classes.minimal}`;
    if (severityLower.includes("mild")) return `${classes.base} ${classes.mild}`;
    // Check "moderate to severe" before "moderate" to avoid partial match
    if (severityLower.includes("moderate to severe")) return `${classes.base} ${classes.severe}`;
    if (severityLower.includes("moderate")) return `${classes.base} ${classes.moderate}`;
    if (severityLower.includes("severe")) return `${classes.base} ${classes.severe}`;
    // AQ-50 specific categories (explicit so future wording changes are intentional)
    if (severityLower.includes("pronounced")) return `${classes.base} ${classes.severe}`;
    if (severityLower.includes("consistent with autism")) return `${classes.base} ${classes.clinically_significant}`;
    // Below threshold - violet to indicate score is below clinical cutoff (used by AQ-50, ASRS, etc.)
    if (severityLower.includes("below threshold")) return `${classes.base} ${classes.below_threshold}`;
    // Threshold crossed - red to indicate score meets/exceeds clinical cutoff (used by ASRS, etc.)
    if (severityLower.includes("threshold crossed")) return `${classes.base} ${classes.severe}`;
    if (severityLower.includes("clinically significant")) return `${classes.base} ${classes.clinically_significant}`;
    if (severityLower.includes("not clinically significant")) return `${classes.base} ${classes.not_clinically_significant}`;
    if (severityLower.includes("normal")) return `${classes.base} ${classes.normal}`;
    if (severityLower.includes("at-risk")) return `${classes.base} ${classes.at_risk}`;
    
    return `${classes.base} ${classes.default}`;
  };

  // Render a compact progress bar for score visualization
  const renderCompactScoreBar = (scale, score, maxScore) => {
    // Default max scores for known scales
    // Keep in sync with maxScore values returned by scoring functions in ScalesFunctions.js
    const defaultMaxScores = {
      'PHQ-9': 27,
      'GAD-7': 21,
      'ESS': 24,
      'MDQ': 13,
      'AQ-10': 10,
      'AQ-50': 50,
      'CAT-Q': 175,
      'CORE-10': 40,
      'ASRS': 6, // Part A score (0-6) is used for display, not total
      'CNST': 5,
      'SPAT': 4,
      'BAARS-IV Current': 72,
      'BAARS-IV Childhood': 72,
      'BAARS-IV Current - Informant': 72,
      'BAARS-IV Childhood - Informant': 72,
      'ASRS-O Informant': 72,
      'SNAP-IV Childhood - Informant': 54,
      'SNAP-IV Childhood': 54,
      'Y-BOCS': 40,
      'AUDIT-C': 12,
    };
    
    const max = maxScore || defaultMaxScores[scale] || 100;
    const percentage = (score / max) * 100;
    
    return (
      <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
        <div 
          className="h-2 rounded-full" 
          style={{ 
            width: `${Math.min(percentage, 100)}%`,
            backgroundColor: getSeverityBarColor(scale, score)
          }}
        ></div>
      </div>
    );
  };

  /**
   * Aggregates any written feedback left by informants when they completed
   * their questionnaires.
   *
   * Design intent:
   * - Informant feedback is conceptually tied to informant measures, so we
   *   surface it from the Informant Scales section rather than the patient
   *   history panel.
   * - We rely on the `question` metadata stored at submit time (see
   *   AllFormsCompletedFeedbackModal) where present, but gracefully handle
   *   older records that only have free-text comments.
   */
  const informantFeedbackEntries = useMemo(() => {
    const entries = [];

    try {
      Object.entries(informantData || {}).forEach(([informantId, inf]) => {
        const ff = inf?.formFeedback;
        const text = typeof ff?.feedback === 'string' ? ff.feedback.trim() : '';
        if (!text) return;

        const question =
          typeof ff?.question === 'string' && ff.question.trim()
            ? ff.question.trim()
            : null;

        const displayName =
          inf.displayName ||
          [inf.firstName, inf.lastName].filter(Boolean).join(' ') ||
          inf.email ||
          'Informant';

        // Provider-side metadata about the informant relationship and assigned
        // life stages lives on the primary patient document. We pull it in here
        // so the feedback dialog can give clinicians enough context to interpret
        // what perspective each comment is coming from.
        const informantMeta = patientData?.informants?.[informantId] || {};
        const relationship =
          informantMeta.informantRelationshipToPatient ||
          inf.relationship ||
          '';
        const lifeStages = Array.isArray(informantMeta.assignedForLifeStages)
          ? informantMeta.assignedForLifeStages
          : [];

        entries.push({
          id: informantId,
          name: displayName,
          relationship,
          lifeStages,
          question,
          feedback: text,
          submittedAtLabel: formatDateTime(ff?.submittedAt),
        });
      });
    } catch (e) {
      // Defensive: issues in optional feedback metadata should never break scale UI.
      // eslint-disable-next-line no-console
      console.warn('Failed to aggregate informant feedback entries for PatientScalesPanel:', e);
    }

    return entries;
  }, [informantData, patientData]);

  const hasInformantFeedback = informantFeedbackEntries.length > 0;

  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (num) => {
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return 'th';
    }
    
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Helper function to get a display score for scales that don't have a single score
  // Note: scaleKey parameter is optional but helpful for informant scales where internalName may differ
  const getDisplayScore = (scaleResults, scaleName, scaleKey = null) => {
    // Completed-but-unscored scales reach here with null; never dereference.
    if (!scaleResults || typeof scaleResults !== 'object') return null;

    // ASRS-O Informant: use total score (0-72) - dimensional measure, no threshold
    // Check both scaleName AND scaleKey since internalName may be "Their Symptoms in Adulthood"
    const isAsrsOInformant = scaleName.includes('ASRS-O') || 
                             scaleName === 'ASRS-O Informant' || 
                             scaleKey === 'asrsOAdulthoodInformant' ||
                             scaleResults?.scale === 'ASRS-O Informant';
    if (isAsrsOInformant) {
      return scaleResults.score;
    }

    // ASRS (patient): use Part A score (0-6) for display since that's what the threshold is based on
    if ((scaleName.includes('ASRS') || scaleName.includes('Adult ADHD Self-Report Scale')) && 
        typeof scaleResults.partAScore === 'number') {
      return scaleResults.partAScore;
    }

    // For scales with a single score property
    if (scaleResults.score !== undefined) {
      return scaleResults.score;
    }

    // SNAP-IV: use totalScore (0-54)
    if (scaleName.includes('SNAP-IV') && scaleResults.totalScore !== undefined) {
      return scaleResults.totalScore;
    }
    
    // For WEISS - return percentage of impaired sections (for progress bar)
    if (scaleName.includes('WEISS') && scaleResults.totalImpairedSections !== undefined && scaleResults.totalValidSections > 0) {
      return Math.round((scaleResults.totalImpairedSections / scaleResults.totalValidSections) * 100);
    }
    
    // For BDEFS - return ADHD likelihood percentile if available (for progress bar)
    if (scaleName.includes('BDEFS') && scaleResults.summary?.adhdLikelihood?.percentile !== undefined) {
      // Handle percentile ranges by taking the average
      const percentile = scaleResults.summary.adhdLikelihood.percentile;
      if (Array.isArray(percentile)) {
        return Math.round((percentile[0] + percentile[1]) / 2);
      }
      return percentile;
    }
    
    // Fallback to null
    return null;
  };

  // Helper function to get display text for multi-section scales
  // Note: scaleKey parameter is optional but helpful for informant scales where internalName may differ
  const getDisplayText = (scaleResults, scaleName, scaleKey = null) => {
    // Completed-but-unscored scales reach here with null; never dereference.
    if (!scaleResults || typeof scaleResults !== 'object') return 'Completed';

    // CAT-Q: in the overview row we show only whether the threshold was crossed.
    // We still keep the numeric score in scaleResults for audit/export and for the
    // detailed view panel, but clinicians asked for a quick binary signal here.
    if (scaleName.includes('CAT-Q') || scaleName.includes('Camouflaging Autistic Traits Questionnaire')) {
      if (scaleResults.isHighCamouflaging === true) return 'Threshold crossed (≥100)';
      if (scaleResults.isHighCamouflaging === false) return 'Below threshold (<100)';
      return 'Completed';
    }

    // ASRS-O Informant: dimensional measure with total Likert score (0-72), no threshold
    // Check both scaleName AND scaleKey since internalName may be "Their Symptoms in Adulthood"
    const isAsrsOInformant = scaleName.includes('ASRS-O') || 
                             scaleName === 'ASRS-O Informant' || 
                             scaleKey === 'asrsOAdulthoodInformant' ||
                             scaleResults?.scale === 'ASRS-O Informant';
    if (isAsrsOInformant) {
      const total = typeof scaleResults.score === 'number' ? scaleResults.score : null;
      if (total !== null) return `${total}`;
      return 'Completed';
    }

    // ASRS (patient): Part A (Q1-6) is the clinically relevant screening threshold (≥4).
    // Display as simple "X out of 6" format
    if (scaleName.includes('ASRS') || scaleName.includes('Adult ADHD Self-Report Scale')) {
      const partA = typeof scaleResults.partAScore === 'number' ? scaleResults.partAScore : null;
      if (partA !== null) return `${partA} out of 6`;
      return 'Completed';
    }

    // CNST: multi-condition scale - show "Click for breakdown" prompt
    // Detailed condition-by-condition breakdown is shown in the expanded section
    if (scaleName.includes('CNST') || scaleName.includes('Combined Neurodevelopmental Screening Tool')) {
      return 'CNST_CLICK_FOR_BREAKDOWN'; // Special marker for custom rendering
    }

    // SPAT: multi-quadrant scale - show "Click for breakdown" prompt
    // Detailed quadrant-by-quadrant breakdown is shown in the expanded section
    if (scaleName.includes('SPAT') || scaleName.includes('Sensory Profile Assessment Tool')) {
      return 'SPAT_CLICK_FOR_BREAKDOWN'; // Special marker for custom rendering
    }

    // For scales with a single score property
    if (scaleResults.score !== undefined) {
      return scaleResults.score.toString();
    }

    // SNAP-IV: show only total score (to match BAARS display)
    if (scaleName.includes('SNAP-IV')) {
      const total = scaleResults.totalScore;
      if (typeof total === 'number') return `${total}`;
      return 'Completed';
    }
    
    // For WEISS - show impaired sections out of total
    if (scaleName.includes('WEISS') && scaleResults.totalImpairedSections !== undefined) {
      return `${scaleResults.totalImpairedSections} of ${scaleResults.totalValidSections} domains impaired`;
    }
    
    // For BDEFS - show ADHD likelihood percentile if available
    if (scaleName.includes('BDEFS') && scaleResults.summary?.adhdLikelihood?.percentile !== undefined) {
      const percentile = scaleResults.summary.adhdLikelihood.percentile;
      if (Array.isArray(percentile)) {
        // For percentile ranges, show as "85th-90th percentile"
        return `${percentile[0]}${getOrdinalSuffix(percentile[0])}-${percentile[1]}${getOrdinalSuffix(percentile[1])} percentile`;
      }
      // For single percentiles, show as "85th percentile"
      return `ADHD Likelihood: ${percentile}${getOrdinalSuffix(percentile)} percentile`;
    }
    
    // Fallback
    return 'Completed';
  };

  // Get color for severity bar
  const getSeverityBarColor = (scale, score) => {
    // Handle WEISS - score is now percentage of impaired domains
    if (scale.includes('WEISS')) {
      if (score === 0) return '#4ade80'; // green - no impairment
      if (score <= 25) return '#facc15'; // yellow - low impairment
      if (score <= 50) return '#fb923c'; // orange - moderate impairment
      return '#ef4444'; // red - high impairment
    }
    
    // Handle BDEFS - score is now percentile
    if (scale.includes('BDEFS')) {
      if (score <= 50) return '#4ade80'; // green - below average
      if (score <= 75) return '#facc15'; // yellow - above average
      if (score <= 89) return '#fb923c'; // orange - well above average
      return '#ef4444'; // red - very high (90th+ percentile)
    }
    
    // Different coloring logic for different scales
    if (scale === 'PHQ-9') {
      if (score <= 4) return '#4ade80'; // green
      if (score <= 9) return '#facc15'; // yellow
      if (score <= 14) return '#fb923c'; // orange
      if (score <= 19) return '#f87171'; // light red
      return '#ef4444'; // red
    }
    
    if (scale === 'GAD-7') {
      if (score <= 4) return '#4ade80'; // green
      if (score <= 9) return '#facc15'; // yellow
      if (score <= 14) return '#fb923c'; // orange
      return '#ef4444'; // red
    }

    if (scale === 'Y-BOCS') {
    if (score < 8) return '#4ade80'; // green - subclinical
    if (score <= 15) return '#facc15'; // yellow - mild
    if (score <= 23) return '#fb923c'; // orange - moderate
    if (score <= 31) return '#f87171'; // light red - severe
    return '#ef4444'; // red - extreme
  }    

    // BAARS-IV uses a binary clinical significance threshold of 48 (out of 72)
    // No validated severity bands exist, so we use violet (neutral) below threshold
    // and red at/above threshold to indicate clinical significance
    if (scale === 'BAARS-IV Current' || scale === 'BAARS-IV Current - Informant' ||
        scale === 'BAARS-IV Childhood' || scale === 'BAARS-IV Childhood - Informant') {
      if (score < 48) return '#8b5cf6'; // violet - below clinical threshold
      return '#ef4444'; // red - clinically significant (≥48)
    }

    if (scale === 'MDQ') {
      // MDQ scoring is based on number of "yes" symptoms (0-13)
      // 7+ symptoms is considered clinically significant when combined with other criteria
      if (score < 7) return '#4ade80'; // green
      return '#fb923c'; // orange
    }
    
    if (scale === 'AUDIT-C') {
      if (score < 3) return '#4ade80'; // green - normal
      return '#fb923c'; // orange - at-risk
    }

    // CORE-10 (clinical score 0-40)
    if (scale === 'CORE-10') {
      if (score <= 5) return '#4ade80'; // Healthy
      if (score <= 10) return '#facc15'; // Low level distress
      if (score <= 14) return '#fb923c'; // Mild
      if (score <= 19) return '#f87171'; // Moderate
      if (score <= 24) return '#ef4444'; // Moderate to severe
      return '#dc2626'; // Severe
    }

    // AQ-50 uses three thresholds based on combined (non-sex-specific) cutoffs:
    // < 26: Below threshold, 26-35: Consistent with Autism, ≥ 36: Pronounced
    if (scale === 'AQ-50') {
      if (score < 26) return '#8b5cf6'; // violet - below threshold
      if (score < 36) return '#fb923c'; // orange - consistent with autism
      return '#ef4444'; // red - pronounced
    }

    // ASRS uses Part A score (0-6) with threshold of ≥4
    // score here is partAScore, not total score
    if (scale === 'ASRS') {
      if (score < 4) return '#8b5cf6'; // violet - below threshold
      return '#ef4444'; // red - threshold crossed
    }
    
    // Default color scheme for other scales
    return '#8b5cf6'; // purple
  };

  return (
    <div className="flex flex-col gap-4 mb-4">
    <div className="text-left w-full p-5 bg-white rounded-xl shadow border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Patient Self-Report Scales</h2>
      </div>
      {displayMode !== 'condensed' && (
      <p className="text-left text-sm text-gray-600 mb-4">
        Click on any scale to view responses from the patient.
      </p>
      )}
      
      {sortedPatientScales.length > 0 ? (
        <div className="border rounded-lg divide-y">
          {sortedPatientScales.map(scale => {
            const isExpanded = expandedRows[scale.key];
            const scaleResults = scale.scaleResults;
            const isCompleted = scale.completed;
            const scaleName = scale.internalName || "";
            const scaleTooltip = scale.tooltipText || "";            
            
            return (
              <div 
                key={scale.key} 
                className={`transition-all duration-200 ${isCompleted ? 'bg-white' : 'bg-gray-50'}`}
              >
                {/* Collapsible header row */}
                {/* Mobile-first responsive layout */}
                <div 
                  className={`px-3 py-3 ${(isCompleted || scale.isStarted) ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={() => (isCompleted || scale.isStarted) && toggleRowExpansion(scale.key)}
                >
                  {/* Mobile layout: stacked */}
                  <div className="flex flex-col gap-2 md:hidden">
                    {/* Top row: Scale name and expand icon */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1 min-w-0">
                        <ChevronRightIcon 
                          className={`h-4 w-4 ${(isCompleted || scale.isStarted) ? 'text-gray-500' : 'text-gray-300'} transition-transform ${isExpanded ? 'rotate-90' : ''} flex-shrink-0`} 
                        />
                        {/* Green checkmark icon for completed scales - provides visual completion indicator
                            especially important for CNST/SPAT which don't show progress bars */}
                        {isCompleted && (
                          <div className="flex items-center justify-center h-5 w-5 rounded-full bg-green-100 ml-1 flex-shrink-0">
                            <CheckIcon className="h-3 w-3 text-green-600" />
                          </div>
                        )}
                        <h3 className={`font-medium ${(isCompleted || scale.isStarted) ? 'text-gray-900' : 'text-gray-500'} ml-2 truncate`}>
                          {scaleName}                        
                        </h3>
                      </div>
                      {/* Action buttons on right */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <div 
                          className="flex items-center justify-center w-8 h-8 text-violet-800 rounded-full hover:bg-violet-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();                              
                            handleFormClick({
                              key: scale.key,
                              source: 'patient',
                              scaleMetadata: {
                                internalName: scale.internalName,
                                scaleResults: scale.scaleResults,
                                interpretation: scale.scaleResults?.interpretation,
                                impairment: scale.scaleResults?.impairment
                              }
                            });                              
                          }}
                        >
                          <SearchIcon className="h-5 w-5" />
                        </div>
                        {scaleTooltip && (
                          <div 
                            className="flex items-center justify-center w-8 h-8 text-violet-800 rounded-full hover:bg-violet-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <InfoTooltip 
                              content={scaleTooltip} 
                              label={`Information about ${scaleName}`}
                              maxWidth="300px"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Bottom row: Score and severity */}
                    <div className="flex items-center justify-between ml-6">
                      <div className="flex items-center flex-1">
                        {isCompleted && hasDisplayableScaleResults(scaleResults) ? (
                          <div className="flex items-center gap-2 flex-1">
                            {/* CNST/SPAT: no progress bar, show "Click for breakdown" in italics */}
                            {(scaleName.includes('CNST') || scaleName.includes('Combined Neurodevelopmental Screening Tool') ||
                              scaleName.includes('SPAT') || scaleName.includes('Sensory Profile Assessment Tool')) ? (
                              <span className="text-sm text-gray-600 italic">Click for breakdown</span>
                            ) : (
                              <>
                                <div className="flex-1 max-w-32">
                                  {renderCompactScoreBar(scaleName, getDisplayScore(scaleResults, scaleName))}
                                </div>
                                <span className={`text-sm font-medium ${getSeverityTextColor(scaleName, getDisplayScore(scaleResults, scaleName))} flex-shrink-0`}>
                                  {getDisplayText(scaleResults, scaleName)}
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {/* Completed-but-unscored (null scaleResults) must not look like "Not started". */}
                            {isCompleted ? (
                              <span className="text-xs text-gray-500 italic">Completed</span>
                            ) : scale.isStarted && scale.progress?.progressPercentage > 0 ? (
                              <span className="text-xs font-medium text-gray-500">
                                {Math.round(scale.progress.progressPercentage)}% complete
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 italic">Not started</span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Severity badge - hidden for BAARS-IV, CNST, SPAT (breakdown shown in expanded section) */}
                      {isCompleted && scaleResults?.severity && 
                       !scaleName.includes('BAARS-IV') && 
                       !scaleName.includes('CNST') && !scaleName.includes('Combined Neurodevelopmental Screening Tool') &&
                       !scaleName.includes('SPAT') && !scaleName.includes('Sensory Profile Assessment Tool') && (
                        <span className={`${getSeverityClass(scaleName, scaleResults.severity)} flex-shrink-0 ml-2`}>
                          {scaleResults.severity}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Desktop layout: single row */}
                  <div className="hidden md:flex md:items-center md:justify-between">
                    {/* Left section: Expand icon + Scale name - responsive width */}
                    <div className="flex items-center min-w-0 flex-shrink-0" style={{flexBasis: '220px'}}>
                    <ChevronRightIcon 
                      className={`h-4 w-4 ${(isCompleted || scale.isStarted) ? 'text-gray-500' : 'text-gray-300'} transition-transform ${isExpanded ? 'rotate-90' : ''} flex-shrink-0`} 
                    />
                    {/* Green checkmark icon for completed scales - provides visual completion indicator
                        especially important for CNST/SPAT which don't show progress bars */}
                    {isCompleted && (
                      <div className="flex items-center justify-center h-5 w-5 rounded-full bg-green-100 ml-1 flex-shrink-0">
                        <CheckIcon className="h-3 w-3 text-green-600" />
                      </div>
                    )}
                      <div className="ml-2 min-w-0">
                      <h3 className={`font-medium ${(isCompleted || scale.isStarted) ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                        {scaleName}                        
                      </h3>                      
                    </div>                    
                  </div>
                  
                  {/* Middle section: Score bar + value */}
                    <div className="flex items-center flex-grow mx-4 min-w-0">
                    {isCompleted && hasDisplayableScaleResults(scaleResults) ? (
                      <>
                        {/* CNST/SPAT: no progress bar, show "Click for breakdown" in italics */}
                        {(scaleName.includes('CNST') || scaleName.includes('Combined Neurodevelopmental Screening Tool') ||
                          scaleName.includes('SPAT') || scaleName.includes('Sensory Profile Assessment Tool')) ? (
                          <span className="text-sm text-gray-600 italic">Click for breakdown</span>
                        ) : (
                          <>
                            {renderCompactScoreBar(scaleName, getDisplayScore(scaleResults, scaleName))}
                            <span className={`text-sm font-medium ml-2 ${getSeverityTextColor(scaleName, getDisplayScore(scaleResults, scaleName))}`}>
                              {getDisplayText(scaleResults, scaleName)}
                            </span>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        {/* Completed-but-unscored (null scaleResults) must not look like "Not started". */}
                        {isCompleted ? (
                          <span className="text-xs text-gray-500 italic">Completed</span>
                        ) : scale.isStarted && scale.progress?.progressPercentage > 0 ? (
                          <span className="text-xs font-medium text-gray-500">
                            {Math.round(scale.progress.progressPercentage)}% complete
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500 italic">Not started</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Right section: Status badge + source button */}
                    <div className="flex items-center flex-shrink-0">
                    {/* Severity badge - hidden for BAARS-IV, CNST, SPAT (breakdown shown in expanded section) */}
                    {isCompleted && scaleResults?.severity && 
                     !scaleName.includes('BAARS-IV') && 
                     !scaleName.includes('CNST') && !scaleName.includes('Combined Neurodevelopmental Screening Tool') &&
                     !scaleName.includes('SPAT') && !scaleName.includes('Sensory Profile Assessment Tool') && (
                      <span className={`mr-2 ${getSeverityClass(scaleName, scaleResults.severity)}`}>
                        {scaleResults.severity}
                      </span>
                    )}                                      

                    <div 
                      className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100"
                      onClick={(e) => {
                        e.stopPropagation();                              
                          handleFormClick({
                            key: scale.key,
                        source: 'patient',
                        scaleMetadata: {
                          internalName: scale.internalName,
                          scaleResults: scale.scaleResults,
                          interpretation: scale.scaleResults?.interpretation,
                          impairment: scale.scaleResults?.impairment
                        }
                          });                              
                      }}
                    >
                      <SearchIcon className="h-3 w-3" />
                    </div>

                      {scaleTooltip && (
                        <div 
                        className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <InfoTooltip 
                          content={scaleTooltip} 
                          label={`Information about ${scaleName}`}
                          maxWidth="300px"
                        />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expandable content — animated via CSS grid row transition.
                    The outer grid container transitions between 0fr and 1fr,
                    giving a smooth height animation without JS measurement. */}
                {(isCompleted || scale.isStarted) && scaleResults && (
                  <div
                    className="transition-[grid-template-rows] duration-300 ease-in-out"
                    style={{ display: 'grid', gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                  >
                  <div className="overflow-hidden">
                  <div className="px-10 py-3 bg-gray-50 border-t border-gray-100">
                    {/* WEISS Chart */}
                    {scale.key === 'weiss' && scaleResults.sectionResults && (
                      <WeissBarChart scaleResults={scaleResults} />
                    )}
                    
                    {/* BDEFS Chart */}
                    {scale.key === 'bdefs' && scaleResults.sectionResults && (
                      <BdefsPercentileChart scaleResults={scaleResults} />
                    )}

                    {/* CNST Breakdown - condition-by-condition display */}
                    {scale.key === 'cnst' && scaleResults.scores && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Condition Breakdown</h4>
                        <div className="grid gap-2">
                          {Object.entries(scaleResults.scores).map(([conditionKey, condition]) => {
                            // Map condition keys to display names
                            const conditionNames = {
                              dyslexia: 'Dyslexia',
                              dyscalculia: 'Dyscalculia',
                              dcd: 'DCD (Dyspraxia)',
                              tics: 'Tics',
                              dld: 'DLD (Language Disorder)'
                            };
                            const conditionName = conditionNames[conditionKey] || conditionKey;
                            
                            // Color based on level
                            const levelColors = {
                              high: { bg: 'bg-red-100', text: 'text-red-700', bar: '#ef4444' },
                              moderate: { bg: 'bg-orange-100', text: 'text-orange-700', bar: '#fb923c' },
                              low: { bg: 'bg-green-100', text: 'text-green-700', bar: '#4ade80' }
                            };
                            const colors = levelColors[condition.level] || levelColors.low;
                            
                            return (
                              <div key={conditionKey} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200">
                                {/* Condition name - fixed width for alignment */}
                                <div className="w-40 flex-shrink-0">
                                  <span className="text-sm font-medium text-gray-800">{conditionName}</span>
                                </div>
                                {/* Progress bar - ml-6 aligns with main row progress bars, w-24 matches their width */}
                                <div className="ml-6 w-24 bg-gray-200 rounded-full h-2 flex-shrink-0">
                                  <div 
                                    className="h-2 rounded-full" 
                                    style={{ 
                                      width: `${Math.min(condition.percentage, 100)}%`,
                                      backgroundColor: colors.bar
                                    }}
                                  />
                                </div>
                                {/* Percentage - positioned immediately after bar like score text */}
                                <span className="text-sm font-medium text-gray-600 w-12 flex-shrink-0">
                                  {Math.round(condition.percentage)}%
                                </span>
                                {/* Level badge - ml-auto pushes to right edge to align with other form row badges */}
                                <span className={`ml-auto text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${colors.bg} ${colors.text}`}>
                                  {condition.level_label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {/* Summary of elevated areas - styled like Interpretation section */}
                        {scaleResults.summary && (
                          <div className="mt-3 space-y-1">
                            {scaleResults.summary.elevated_areas?.length > 0 && (
                              <div>
                                <span className="text-sm font-medium text-gray-700">High possibility: </span>
                                <span className="text-sm text-gray-800">{scaleResults.summary.elevated_areas.map(a => {
                                  const names = { dyslexia: 'Dyslexia', dyscalculia: 'Dyscalculia', dcd: 'DCD', tics: 'Tics', dld: 'DLD' };
                                  return names[a] || a;
                                }).join(', ')}</span>
                              </div>
                            )}
                            {scaleResults.summary.areas_for_monitoring?.length > 0 && (
                              <div>
                                <span className="text-sm font-medium text-gray-700">Moderate possibility: </span>
                                <span className="text-sm text-gray-800">{scaleResults.summary.areas_for_monitoring.map(a => {
                                  const names = { dyslexia: 'Dyslexia', dyscalculia: 'Dyscalculia', dcd: 'DCD', tics: 'Tics', dld: 'DLD' };
                                  return names[a] || a;
                                }).join(', ')}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* SPAT Breakdown - quadrant-by-quadrant display */}
                    {scale.key === 'spat' && scaleResults.quadrant_scores && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Sensory Quadrant Breakdown</h4>
                        <div className="grid gap-2">
                          {Object.entries(scaleResults.quadrant_scores).map(([quadrantKey, quadrant]) => {
                            // Map quadrant keys to display names
                            const quadrantNames = {
                              sensitivity: 'Sensitivity',
                              seeking: 'Seeking',
                              avoiding: 'Avoiding',
                              registration: 'Registration'
                            };
                            const quadrantName = quadrantNames[quadrantKey] || quadrantKey;
                            
                            // Color based on level
                            const levelColors = {
                              high: { bg: 'bg-red-100', text: 'text-red-700', bar: '#ef4444' },
                              moderate: { bg: 'bg-orange-100', text: 'text-orange-700', bar: '#fb923c' },
                              typical: { bg: 'bg-green-100', text: 'text-green-700', bar: '#4ade80' },
                              low: { bg: 'bg-blue-100', text: 'text-blue-700', bar: '#60a5fa' }
                            };
                            const colors = levelColors[quadrant.level] || levelColors.typical;
                            
                            return (
                              <div key={quadrantKey} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200">
                                {/* Quadrant name - fixed width for alignment */}
                                <div className="w-40 flex-shrink-0">
                                  <span className="text-sm font-medium text-gray-800">{quadrantName}</span>
                                </div>
                                {/* Progress bar - ml-6 aligns with main row progress bars, w-24 matches their width */}
                                <div className="ml-6 w-24 bg-gray-200 rounded-full h-2 flex-shrink-0">
                                  <div 
                                    className="h-2 rounded-full" 
                                    style={{ 
                                      width: `${Math.min(quadrant.percentage, 100)}%`,
                                      backgroundColor: colors.bar
                                    }}
                                  />
                                </div>
                                {/* Percentage - positioned immediately after bar like score text */}
                                <span className="text-sm font-medium text-gray-600 w-12 flex-shrink-0">
                                  {Math.round(quadrant.percentage)}%
                                </span>
                                {/* Level badge - ml-auto pushes to right edge to align with other form row badges */}
                                <span className={`ml-auto text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${colors.bg} ${colors.text}`}>
                                  {quadrant.level_label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {/* Summary - styled like Interpretation section */}
                        {scaleResults.summary && (
                          <div className="mt-3 space-y-1">
                            {/* Show mixed pattern when 2+ quadrants are elevated, otherwise show dominant quadrant */}
                            {scaleResults.summary.is_mixed_pattern && scaleResults.summary.elevated_quadrants?.length >= 2 ? (
                              <div>
                                <span className="text-sm font-medium text-gray-700">Mixed pattern: </span>
                                <span className="text-sm text-gray-800">{
                                  scaleResults.summary.elevated_quadrants.map(q => 
                                    ({ sensitivity: 'Sensitivity', seeking: 'Seeking', avoiding: 'Avoiding', registration: 'Registration' }[q] || q)
                                  ).join(', ')
                                }</span>
                              </div>
                            ) : scaleResults.summary.dominant_quadrant && (
                              <div>
                                <span className="text-sm font-medium text-gray-700">Dominant quadrant: </span>
                                <span className="text-sm text-gray-800">{
                                  { sensitivity: 'Sensitivity', seeking: 'Seeking', avoiding: 'Avoiding', registration: 'Registration' }[scaleResults.summary.dominant_quadrant] || scaleResults.summary.dominant_quadrant
                                }</span>
                              </div>
                            )}
                            {scaleResults.summary.elevated_systems?.length > 0 && (
                              <div>
                                <span className="text-sm font-medium text-gray-700">Elevated systems: </span>
                                <span className="text-sm text-gray-800">{scaleResults.summary.elevated_systems.map(s => 
                                  s.charAt(0).toUpperCase() + s.slice(1)
                                ).join(', ')}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-sm font-medium text-gray-700">Clinical significance: </span>
                              <span className="text-sm text-gray-800">{scaleResults.summary.clinical_significance ? 'Yes' : 'No'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Interpretation - temporarily hidden for BAARS-IV scales */}
                    {scaleResults.interpretation && !scaleName.includes('BAARS-IV') && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Interpretation: </span>
                        <span className="text-sm text-gray-800">{scaleResults.interpretation}</span>
                      </div>
                    )}
                    
                    {/* Impairment */}
                    {scaleResults.impairment && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Functional Impact: </span>
                        <span className="text-sm text-gray-800">{scaleResults.impairment}</span>
                      </div>
                    )}
                    
                    {/* Updated timestamp format with icon */}
                <div className="text-xs text-gray-500 mb-3 flex items-center">
                  <ClockIcon className="h-3 w-3 mr-1" /> 
                  Last updated: {formatDateTime(scaleResults.lastUpdated)}
                </div>
                  </div>
                  </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 border rounded-lg">
          <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No scales available</h3>
          <p className="mt-1 text-sm text-gray-500">
            No standardised assessment scales have been configured.
          </p>
        </div>
      )}

    <FormDetailsPanel
      isOpen={isPanelOpen && !!selectedForm}
      formInfo={selectedForm}
      patientData={patientData}
      informantData={informantData}
      onClose={handleClosePanel}
      displayMode={displayMode}
    />
    </div>
    
    {/* Hide Informant Scales section for subclinical services, and for pathways
        that display informant history separately (e.g. Adult Autism, which has
        no informant rating scales and shows InformantHistoryPanel elsewhere). */}
    {!isSubclinicalService && !hideInformantScales && (
    <div className="text-left w-full p-5 bg-white rounded-xl shadow border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Informant Scales</h2>
        {hasInformantFeedback && (
          /**
           * Message bubble icon: indicates that one or more informants have left
           * written feedback about their experience of completing questionnaires.
           *
           * We keep this scoped to the Informant Scales panel so clinicians can
           * quickly connect informant narratives with their corresponding
           * standardised measures.
           */
          <button
            type="button"
            onClick={() => setIsInformantFeedbackDialogOpen(true)}
            className="ml-2 mr-2 inline-flex items-center justify-center rounded-full p-1.5 text-primary hover:bg-violet-100 hover:text-violet-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
            aria-label="View informant questionnaire feedback"
          >
            <ChatAltIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      {displayMode !== 'condensed' && (
      <p className="text-left text-sm text-gray-600 mb-4">
          Click on any scale to view responses from the patient's informants.
      </p>      
      )}      

      {(() => {
        // Loading state logic for informant scales
        // We consider it loading if the current patient has informants referenced
        // but we have not yet loaded all corresponding informant documents.
        const expectedInformantIds = patientData?.informants ? Object.keys(patientData.informants) : [];
        const loadedInformantIds = Object.keys(informantData || {});
        const loadedCountForThisPatient = loadedInformantIds.filter(id => expectedInformantIds.includes(id)).length;
        const informantLoading = expectedInformantIds.length > 0 && loadedCountForThisPatient < expectedInformantIds.length;

        // Inline spinner styled like ClinicianDashboard
        const LoadingSpinner = () => (
          <div className="w-full h-24 flex items-center justify-center">
            <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-gray-800 rounded-lg">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="font-semibold">Loading informant scales...</span>
            </div>
          </div>
        );

        if (informantLoading) {
          return <LoadingSpinner />;
        }

        // Determine which life stages the patient skipped (have a skip reason)
        // but are not currently covered by an assigned informant. Used to show a
        // subtle notice beneath the scales grid when only one informant was added
        // and the other stage was intentionally skipped.
        const skippedStages = (() => {
          const reasons = patientData?.informantSkipReasons || {};
          if (Object.keys(reasons).length === 0) return [];

          const coveredStages = new Set();
          if (patientData?.informants) {
            Object.values(patientData.informants).forEach(inf => {
              (inf.assignedForLifeStages || []).forEach(s => coveredStages.add(s));
            });
          }

          const stages = [];
          if (reasons.adulthood && !coveredStages.has('Adulthood')) stages.push('Adulthood');
          if (reasons.childhood && !coveredStages.has('Childhood')) stages.push('Childhood');
          return stages;
        })();

        return (informantData && Object.keys(informantData).length > 0) ? (
        <>
        <div className="border rounded-lg divide-y">
          {sortedInformantScales.map(scale => {
            const isExpanded = expandedRows[scale.key];
            const scaleResults = scale.scaleResults;
            const isCompleted = scale.completed;
            const scaleName = scale.internalName || "";
            const scaleTooltip = scale.tooltipText || "";        
            
            return (
              <div 
                key={scale.key} 
                className={`transition-all duration-200 ${isCompleted ? 'bg-white' : 'bg-gray-50'}`}
              >
                {/* Collapsible header row */}
                {/* Mobile-first responsive layout for informant scales */}
                <div 
                  className={`px-3 py-3 ${(isCompleted || scale.isStarted) ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={() => (isCompleted || scale.isStarted) && toggleRowExpansion(scale.key)}
                >
                  {/* Mobile layout: stacked */}
                  <div className="flex flex-col gap-2 md:hidden">
                    {/* Top row: Scale name and expand icon */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1 min-w-0">
                        <ChevronRightIcon 
                          className={`h-4 w-4 ${(isCompleted || scale.isStarted) ? 'text-gray-500' : 'text-gray-300'} transition-transform ${isExpanded ? 'rotate-90' : ''} flex-shrink-0`} 
                        />
                        {/* Green checkmark icon for completed scales - provides visual completion indicator */}
                        {isCompleted && (
                          <div className="flex items-center justify-center h-5 w-5 rounded-full bg-green-100 ml-1 flex-shrink-0">
                            <CheckIcon className="h-3 w-3 text-green-600" />
                          </div>
                        )}
                        <div className="ml-2 min-w-0">
                          <h3 className={`font-medium ${(isCompleted || scale.isStarted) ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                            {scaleName}
                          </h3>
                          <span className="text-xs font-normal text-violet-600">
                            (Informant)
                          </span>
                        </div>
                      </div>
                      {/* Action buttons on right */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {(isCompleted || scale.isStarted) && (
                          <div 
                            className="flex items-center justify-center w-8 h-8 text-violet-800 rounded-full hover:bg-violet-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFormClick({
                                key: scale.key,
                                informantId: scale.informantId,
                                source: 'informant',
                                scaleMetadata: {
                                  internalName: scale.internalName,
                                  scaleResults: scale.scaleResults,
                                  interpretation: scale.scaleResults?.interpretation,
                                  impairment: scale.scaleResults?.impairment
                                }
                              });
                            }}
                          >
                            <SearchIcon className="h-5 w-5" />
                          </div>
                        )}  
                        {scaleTooltip && (
                          <div 
                            className="flex items-center justify-center w-8 h-8 text-violet-800 rounded-full hover:bg-violet-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <InfoTooltip 
                              content={scaleTooltip} 
                              label={`Information about ${scaleName}`}
                              maxWidth="300px"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Bottom row: Score and severity */}
                    <div className="flex items-center justify-between ml-6">
                      <div className="flex items-center flex-1">
                        {isCompleted && hasDisplayableScaleResults(scaleResults) ? (
                          <div className="flex items-center gap-2 flex-1">
                            <div className="flex-1 max-w-32">
                              {renderCompactScoreBar(scaleName, getDisplayScore(scaleResults, scaleName, scale.key))}
                            </div>
                            <span className={`text-sm font-medium ${getSeverityTextColor(scaleName, getDisplayScore(scaleResults, scaleName, scale.key))} flex-shrink-0`}>
                              {getDisplayText(scaleResults, scaleName, scale.key)}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {/* Completed-but-unscored (null scaleResults) must not look like "Not started". */}
                            {isCompleted ? (
                              <span className="text-xs text-gray-500 italic">Completed</span>
                            ) : scale.isStarted && scale.progress?.progressPercentage > 0 ? (
                              <span className="text-xs font-medium text-gray-500">
                                {Math.round(scale.progress.progressPercentage)}% complete
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 italic">Not started</span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Severity badge - hidden for BAARS-IV, CNST, SPAT (breakdown shown in expanded section) */}
                      {isCompleted && scaleResults?.severity && 
                       !scaleName.includes('BAARS-IV') && 
                       !scaleName.includes('CNST') && !scaleName.includes('Combined Neurodevelopmental Screening Tool') &&
                       !scaleName.includes('SPAT') && !scaleName.includes('Sensory Profile Assessment Tool') && (
                        <span className={`${getSeverityClass(scaleName, scaleResults.severity)} flex-shrink-0 ml-2`}>
                          {scaleResults.severity}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Desktop layout: single row */}
                  <div className="hidden md:flex md:items-center md:justify-between">
                    {/* Left section: Expand icon + Scale name - responsive width */}
                    <div className="flex items-center min-w-0 flex-shrink-0" style={{flexBasis: '220px'}}>
                    <ChevronRightIcon 
                      className={`h-4 w-4 ${(isCompleted || scale.isStarted) ? 'text-gray-500' : 'text-gray-300'} transition-transform ${isExpanded ? 'rotate-90' : ''} flex-shrink-0`} 
                    />
                    {/* Green checkmark icon for completed scales - provides visual completion indicator */}
                    {isCompleted && (
                      <div className="flex items-center justify-center h-5 w-5 rounded-full bg-green-100 ml-1 flex-shrink-0">
                        <CheckIcon className="h-3 w-3 text-green-600" />
                      </div>
                    )}
                      <div className="ml-2 min-w-0">
                      <h3 className={`font-medium ${(isCompleted || scale.isStarted) ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                        {scaleName}
                          <span className="ml-1 text-xs font-normal text-violet-600">
                            (Informant)
                          </span>
                      </h3>                      
                    </div>                    
                  </div>
                  
                  {/* Middle section: Score bar + value */}
                    <div className="flex items-center flex-grow mx-4 min-w-0">
                    {isCompleted && hasDisplayableScaleResults(scaleResults) ? (
                      <>
                        {renderCompactScoreBar(scaleName, getDisplayScore(scaleResults, scaleName, scale.key))}
                          <span className={`text-sm font-medium ml-2 ${getSeverityTextColor(scaleName, getDisplayScore(scaleResults, scaleName, scale.key))}`}>
                          {getDisplayText(scaleResults, scaleName, scale.key)}
                        </span>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        {/* Completed-but-unscored (null scaleResults) must not look like "Not started". */}
                        {isCompleted ? (
                          <span className="text-xs text-gray-500 italic">Completed</span>
                        ) : scale.isStarted && scale.progress?.progressPercentage > 0 ? (
                          <span className="text-xs font-medium text-gray-500">
                            {Math.round(scale.progress.progressPercentage)}% complete
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500 italic">Not started</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Right section: Status badge + source button */}
                    <div className="flex items-center flex-shrink-0">
                    {/* Severity badge - hidden for BAARS-IV, CNST, SPAT (breakdown shown in expanded section) */}
                    {isCompleted && scaleResults?.severity && 
                     !scaleName.includes('BAARS-IV') && 
                     !scaleName.includes('CNST') && !scaleName.includes('Combined Neurodevelopmental Screening Tool') &&
                     !scaleName.includes('SPAT') && !scaleName.includes('Sensory Profile Assessment Tool') && (
                      <span className={`mr-2 ${getSeverityClass(scaleName, scaleResults.severity)}`}>
                        {scaleResults.severity}
                      </span>
                    )}                                      

                    {(isCompleted || scale.isStarted) && (
                          <div 
                            className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100"
                            onClick={(e) => {
                              e.stopPropagation();
                                handleFormClick({
                                  key: scale.key,
                                  informantId: scale.informantId,
                              source: 'informant',
                              scaleMetadata: {
                                internalName: scale.internalName,
                                scaleResults: scale.scaleResults,
                                interpretation: scale.scaleResults?.interpretation,
                                impairment: scale.scaleResults?.impairment
                              }
                            });
                            }}
                          >
                            <SearchIcon className="h-3 w-3" />
                          </div>
                        )}  

                      {scaleTooltip && (
                        <div 
                        className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <InfoTooltip 
                          content={scaleTooltip} 
                          label={`Information about ${scaleName}`}
                          maxWidth="300px"
                        />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expandable content — animated via CSS grid row transition. */}
                {(isCompleted || scale.isStarted) && scaleResults && (
                  <div
                    className="transition-[grid-template-rows] duration-300 ease-in-out"
                    style={{ display: 'grid', gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                  >
                  <div className="overflow-hidden">
                  <div className="px-10 py-3 bg-gray-50 border-t border-gray-100">
                    {/* Interpretation - temporarily hidden for BAARS-IV scales */}
                    {scaleResults.interpretation && !scaleName.includes('BAARS-IV') && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Interpretation: </span>
                        <span className="text-sm text-gray-800">{scaleResults.interpretation}</span>
                      </div>
                    )}
                    
                    {/* Impairment */}
                    {scaleResults.impairment && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Functional Impact: </span>
                        <span className="text-sm text-gray-800">{scaleResults.impairment}</span>
                      </div>
                    )}
                    
                    {/* Updated timestamp format with icon */}
                <div className="text-xs text-gray-500 mb-3 flex items-center">
                  <ClockIcon className="h-3 w-3 mr-1" /> 
                  Last updated: {formatDateTime(scaleResults.lastUpdated)}
                </div>
                  </div>
                  </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Partial-skip notice — shown when at least one informant is present but
            the patient skipped the other life stage. Gives the provider a quick
            inline hint plus a magnifying glass to drill into the skip reason. */}
        {skippedStages.length > 0 && (
          <div className="mt-2 flex items-center justify-center space-x-1.5 py-2">
            <p className="text-xs text-gray-500">
              The patient has skipped adding an informant for {skippedStages.join(' and ')}.
            </p>
            <button
              type="button"
              onClick={() => setIsManageInformantsOpen(true)}
              className="inline-flex items-center justify-center rounded-full p-1 text-violet-600 hover:bg-violet-100 hover:text-violet-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-violet-500"
              title="View skip reasons"
              aria-label="View skip reasons in Manage Informants"
            >
              <SearchIcon className="h-4 w-4" />
            </button>
          </div>
        )}
        </>
        ) : (
        <div className="text-center py-6 border rounded-lg">
          <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No scales available</h3>
          {/* Distinguish between "patient hasn't reached the informant setup step yet"
              and "patient completed setup but chose to skip adding informants". The latter
              shows a clickable magnifying glass so the provider can quickly check the
              patient's stated reasons for skipping. */}
          {patientData?.hasCompletedInformantSetupForm && (!patientData?.informants || Object.keys(patientData.informants).length === 0) ? (
            <div className="mt-1 flex items-center justify-center space-x-1.5">
              <p className="text-sm text-gray-500">
                The patient has skipped adding informants.
              </p>
              <button
                type="button"
                onClick={() => setIsManageInformantsOpen(true)}
                className="inline-flex items-center justify-center rounded-full p-1 text-violet-600 hover:bg-violet-100 hover:text-violet-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-violet-500"
                title="View skip reasons"
                aria-label="View skip reasons in Manage Informants"
              >
                <SearchIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              The patient has not yet nominated any informants.
            </p>
          )}
        </div>
        );
      })()}

      <FormDetailsPanel
        isOpen={isPanelOpen && !!selectedForm}
        formInfo={selectedForm}
        patientData={patientData}
        informantData={informantData}
        onClose={handleClosePanel}
        displayMode={displayMode}
      />
    </div>
    )}

    {/* Informant questionnaire feedback dialog – small centered pop-up */}
    <AlertDialog.Root
      open={isInformantFeedbackDialogOpen}
      onOpenChange={(open) => !open && setIsInformantFeedbackDialogOpen(false)}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-xl max-h-[80vh] overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
          <AlertDialog.Title className="text-lg font-semibold text-gray-900 mb-1">
            Informant Questionnaire Feedback
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-600 mb-4">
            Written comments left by informants after completing their questionnaires.
          </AlertDialog.Description>

          {hasInformantFeedback ? (
            <div className="space-y-4">
              {informantFeedbackEntries.map((entry, index) => (
                <div key={entry.id}>
                  {index > 0 && (
                    <div className="h-px bg-gray-200 mb-3" aria-hidden="true" />
                  )}
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-left">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">
                          {entry.name}
                        </span>
                        {(entry.relationship || (entry.lifeStages && entry.lifeStages.length > 0)) && (
                          <div className="mt-0.5 text-xs text-gray-600 flex flex-wrap items-center gap-1">
                            {entry.relationship && (
                              <span>{entry.relationship}</span>
                            )}
                            {entry.lifeStages && entry.lifeStages.length > 0 && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="font-medium text-gray-700">Assigned to:</span>
                                <div className="flex flex-wrap gap-1">
                                  {entry.lifeStages.map((stage) => (
                                    <span
                                      key={stage}
                                      className="px-1.5 py-0.5 text-[11px] rounded-md bg-violet-100 text-violet-700"
                                    >
                                      {stage}
                                    </span>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.submittedAtLabel || 'Unknown time'}
                      </span>
                    </div>

                    {entry.question && (
                      <p className="mb-1.5 text-xs font-medium text-gray-700">
                        {entry.question}
                      </p>
                    )}

                    <div className="rounded-md bg-white p-2 text-sm text-gray-800 border border-gray-200">
                      {entry.feedback}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              No written feedback has been recorded from informants yet.
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                onClick={() => setIsInformantFeedbackDialogOpen(false)}
                className="inline-flex items-center rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
              >
                Close
              </button>
            </AlertDialog.Cancel>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>

    {/* Manage Informants dialog — opened from the "skipped adding informants"
        empty state so providers can review the patient's stated skip reasons
        without navigating away from the scales view. */}
    <ProviderManageInformantsForm
      isOpen={isManageInformantsOpen}
      onClose={() => setIsManageInformantsOpen(false)}
      patientDataModel={patientData}
      patientId={patientId}
      refreshPatientData={() => fetchPatientData(patientId, true)}
    />
    </div>
  );
}

// Helper function for text coloring
function getSeverityTextColor(scale, score) {
  // Handle WEISS - score is now percentage of impaired domains
  if (scale.includes('WEISS')) {
    if (score === 0) return 'text-green-600'; // no impairment
    if (score <= 25) return 'text-yellow-600'; // low impairment
    if (score <= 50) return 'text-orange-600'; // moderate impairment
    return 'text-red-600'; // high impairment
  }
  
  // Handle BDEFS - score is now percentile
  if (scale.includes('BDEFS')) {
    if (score <= 50) return 'text-green-600'; // below average
    if (score <= 75) return 'text-yellow-600'; // above average
    if (score <= 89) return 'text-orange-600'; // well above average
    return 'text-red-600'; // very high (90th+ percentile)
  }

  if (scale === 'PHQ-9') {
    if (score <= 4) return 'text-green-600';
    if (score <= 9) return 'text-yellow-600';
    if (score <= 14) return 'text-orange-600';
    return 'text-red-600';
  }
  
  if (scale === 'GAD-7') {
    if (score <= 4) return 'text-green-600';
    if (score <= 9) return 'text-yellow-600';
    if (score <= 14) return 'text-orange-600';
    return 'text-red-600';
  }

  // BAARS-IV uses a binary clinical significance threshold of 48 (out of 72)
  // No validated severity bands exist, so we use violet (neutral) below threshold
  // and red at/above threshold to indicate clinical significance
  if (scale === 'BAARS-IV Current' || scale === 'BAARS-IV Current - Informant' ||
      scale === 'BAARS-IV Childhood' || scale === 'BAARS-IV Childhood - Informant') {
    if (score < 48) return 'text-violet-600'; // below clinical threshold
    return 'text-red-600'; // clinically significant (≥48)
  }

  if (scale === 'MDQ') {
    // MDQ scoring is based on number of "yes" symptoms (0-13)
    // 7+ symptoms is considered clinically significant when combined with other criteria
    if (score < 7) return 'text-green-600';
    return 'text-orange-600';
  }

  if (scale === 'Y-BOCS') {
    if (score < 8) return 'text-green-600'; // subclinical
    if (score <= 15) return 'text-yellow-600'; // mild
    if (score <= 23) return 'text-orange-600'; // moderate
    if (score <= 31) return 'text-red-500'; // severe
    return 'text-red-600'; // extreme
  }
  
  if (scale === 'ESS') {
    if (score <= 13) return 'text-green-600';
    if (score <= 26) return 'text-yellow-600';
    if (score <= 39) return 'text-orange-600';
    return 'text-red-600';
  }  
  
  if (scale === 'AUDIT-C') {
    if (score < 3) return 'text-green-600'; // normal
    return 'text-orange-600'; // at-risk
  }

  if (scale === 'CORE-10') {
    if (score <= 5) return 'text-green-600';
    if (score <= 10) return 'text-yellow-600';
    if (score <= 14) return 'text-orange-600';
    return 'text-red-600';
  }

  // AQ-50 uses three thresholds based on combined (non-sex-specific) cutoffs:
  // < 26: Below threshold, 26-35: Consistent with Autism, ≥ 36: Pronounced
  if (scale === 'AQ-50') {
    if (score < 26) return 'text-violet-600'; // below threshold
    if (score < 36) return 'text-orange-600'; // consistent with autism
    return 'text-red-600'; // pronounced
  }

  // ASRS uses Part A score (0-6) with threshold of ≥4
  // score here is partAScore, not total score
  if (scale === 'ASRS') {
    if (score < 4) return 'text-violet-600'; // below threshold
    return 'text-red-600'; // threshold crossed
  }
  
  return 'text-violet-600';
}
